import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Inbox } from 'lucide-react';
import type { PortalIssue } from '@raptor/shared';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { IssueTypeBadge } from '@/components/IssueTypeBadge';
import { StatusBadge } from '@/components/StatusBadge';
import { useCustomers } from '@/features/admin-customers/api';
import { useIssues } from '../api';
import type { IssueListFilters } from '../types';

const PAGE_SIZE = 25;

interface Props {
  isAdmin: boolean;
  // When set, narrows the admin list to a single customer's issues. The
  // Customer column auto-hides — it would be redundant since every row is
  // for the same customer. Non-admin callers pass it through too, but the
  // backend silently ignores it for them.
  customerIdFilter?: string;
}

export function IssueListTable({ isAdmin, customerIdFilter }: Props) {
  const [status, setStatus] = useState<'open' | 'closed' | 'all'>('all');
  const [issueType, setIssueType] = useState<'bug' | 'feature' | 'all'>('all');
  const [offset, setOffset] = useState(0);

  const filters: IssueListFilters = {
    status,
    issueType,
    limit: PAGE_SIZE,
    offset,
    customerId: customerIdFilter,
  };

  const issues = useIssues(filters);
  // Admin-only enrichment for the Customer column. The hook must be called on
  // every render to satisfy rules-of-hooks, but pass enabled:false when not
  // needed (non-admin OR single-customer view) so the underlying
  // /admin/customers request doesn't fire (and doesn't 403 in the console).
  const showCustomerColumn = isAdmin && !customerIdFilter;
  const customers = useCustomers('', { enabled: showCustomerColumn });
  const customerNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (showCustomerColumn && customers.data) {
      for (const c of customers.data.customers) {
        map.set(c.id, c.name || c.companySlug);
      }
    }
    return map;
  }, [showCustomerColumn, customers.data]);

  // Columns are computed each render (they close over customerNameById);
  // cheap enough not to memoise.
  const columns: DataTableColumn<PortalIssue>[] = [
    {
      id: 'title',
      header: 'Title',
      cell: (issue) => (
        <Link
          to={`/issues/${issue.id}`}
          className="font-medium text-primary hover:underline"
          data-testid={`bug-list-row-title-${issue.id}`}
        >
          {issue.title}
        </Link>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (issue) => <IssueTypeBadge type={issue.issueType} />,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (issue) => (
        <StatusBadge
          variant={issue.status === 'open' ? 'success' : 'neutral'}
          label={issue.status === 'open' ? 'Open' : 'Closed'}
          data-status={issue.status}
        />
      ),
    },
    {
      id: 'posts',
      header: 'Posts',
      cell: (issue) => (
        <span
          className="text-sm text-muted-foreground"
          data-testid={`bug-list-row-posts-${issue.id}`}
        >
          {issue.commentCount}
        </span>
      ),
    },
    {
      id: 'submitted',
      header: 'Submitted',
      cell: (issue) => (
        <span className="text-sm text-muted-foreground">
          {formatDistanceToNow(new Date(issue.createdAt), { addSuffix: true })}
        </span>
      ),
    },
  ];
  if (showCustomerColumn) {
    columns.push({
      id: 'customer',
      header: 'Customer',
      cell: (issue) => (
        <span className="text-sm">
          {customerNameById.get(issue.customerId) ?? issue.customerId.slice(0, 8)}
        </span>
      ),
    });
  }

  return (
    <div className="space-y-4" data-testid="bug-list-root">
      <div className="flex flex-wrap items-center gap-3">
        <FilterSelect
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v as typeof status);
            setOffset(0);
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' },
          ]}
        />
        <FilterSelect
          label="Type"
          value={issueType}
          onChange={(v) => {
            setIssueType(v as typeof issueType);
            setOffset(0);
          }}
          options={[
            { value: 'all', label: 'All types' },
            { value: 'bug', label: 'Bug' },
            { value: 'feature', label: 'Feature' },
          ]}
        />
      </div>

      <DataTable
        columns={columns}
        rows={issues.data?.items}
        getRowKey={(issue) => issue.id}
        isLoading={issues.isLoading}
        isError={issues.isError}
        errorState={
          <div className="text-center">
            <div className="text-sm text-destructive">Couldn&apos;t load issues.</div>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => issues.refetch()}>
              Retry
            </Button>
          </div>
        }
        emptyState={
          <EmptyState
            Icon={Inbox}
            title="No issues yet"
            action={
              <Button variant="link" asChild>
                <Link to="/issues/new">Submit your first issue</Link>
              </Button>
            }
          />
        }
        rowProps={(issue) => ({
          'data-testid': `issue-row-${issue.id}`,
          'data-issue-id': issue.id,
        })}
      />

      {issues.isSuccess && (issues.data?.total ?? 0) > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, issues.data?.total ?? 0)} of{' '}
            {issues.data?.total ?? 0}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= (issues.data?.total ?? 0)}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">{label}:</span>
      <div className="w-40">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
